export interface TransferableConverter {
	toTransferable<T>(value: T): T;
}

export interface CachedFunction {
	__cachedFunctionId: string;
}

function isCachedFunction(value: unknown): value is CachedFunction {
	return (
		typeof value === "object" &&
		!!value &&
		!!(value as CachedFunction).__cachedFunctionId
	);
}

function cloneObjectWithConversion<T>(
	value: T,
	converter: (value: unknown) => unknown,
): T {
	const convertedValue = converter(value) as T;

	if (typeof convertedValue === "object") {
		// Why does javascript define `typeof null` as `object` is a great mystery
		if (convertedValue == null) {
			return null as T;
		}

		if (Array.isArray(convertedValue)) {
			const arrayCopy = [...convertedValue];
			for (let i = 0; i < arrayCopy.length; i++) {
				arrayCopy[i] = cloneObjectWithConversion(arrayCopy[i], converter);
			}
			return arrayCopy as T;
		}

		const objectCopy = { ...convertedValue } as Record<string, unknown>;
		for (const [oKey, oValue] of Object.entries(objectCopy)) {
			objectCopy[oKey] = cloneObjectWithConversion(oValue, converter);
		}
		return objectCopy as T;
	}

	return convertedValue;
}

type AnyFunction = (...args: unknown[]) => unknown;

// Converts non-transferable objects (like Functions) to transferable by creating a cached representation
// that can be converted back to the original representation by another function call.
export function createTransferableConverter(): TransferableConverter {
	const functionsByInstance = new Map<AnyFunction, CachedFunction>();
	const functionsById = new Map<string, AnyFunction>();

	// TODO: Handle the removal of cached entries when they are no longer used... this is no trivial matter
	// since we don't know when a cached entry is no longer required, maybe we can do it when the callbacks are
	// unregistered by worker-ui-sdk ?

	return {
		toTransferable<T>(value: T): T {
			return cloneObjectWithConversion(value, (value) => {
				if (typeof value === "function") {
					const existingEntry = functionsByInstance.get(value as AnyFunction);
					if (existingEntry) {
						return existingEntry;
					}

					const newEntry: CachedFunction = {
						__cachedFunctionId: crypto.randomUUID(),
					};

					functionsByInstance.set(value as AnyFunction, newEntry);
					functionsById.set(newEntry.__cachedFunctionId, value as AnyFunction);

					return newEntry;
				}

				return value;
			});
		},
	};
}
